import { Controller } from '@hotwired/stimulus'
import {
  getAnchorOffset,
  resolveTargetFromHref,
  scrollToHash
} from '../utils/anchor_scroll.js'

/**
 * Подсветка активного раздела в сайдбаре личного кабинета.
 *
 * Разделы кабинета — секции одной страницы, поэтому пункт меню подсвечивается
 * по позиции скролла, а не по адресу. Считаем на скролле, а не через
 * IntersectionObserver: отступ сверху зависит от высоты шапки, а она меняется
 * при прокрутке (шапка сжимается) — наблюдателю пришлось бы пересоздавать
 * rootMargin на каждое изменение.
 *
 * @extends {Controller}
 */
export default class AccountNavController extends Controller {
  /**
   * Пункты меню-якоря. Ссылка на страницу турниров и выход целями не помечены.
   *
   * @type {string[]}
   */
  static targets = ['item']

  /**
   * Пары «пункт меню → секция страницы» в порядке следования секций.
   *
   * @type {{ item: HTMLAnchorElement, section: HTMLElement }[]}
   */
  #pairs = []

  /**
   * Подсвеченный сейчас пункт.
   *
   * @type {HTMLAnchorElement | null}
   */
  #active = null

  connect() {
    this.#pairs = this.itemTargets
      .map((item) => ({
        item,
        section: resolveTargetFromHref(item.getAttribute('href'))
      }))
      .filter((pair) => pair.section)

    if (this.#pairs.length === 0) return

    document.addEventListener('scroll', this.#onScroll, { passive: true })
    this.#onScroll()
  }

  disconnect() {
    document.removeEventListener('scroll', this.#onScroll)
    this.#pairs = []
    this.#active = null
  }

  /**
   * Плавно прокручивает к секции по клику, чтобы переход не «прыгал» под шапку.
   *
   * @param {MouseEvent} event — клик по пункту меню
   */
  scroll(event) {
    const href = event.currentTarget?.getAttribute('href')
    if (!href || !resolveTargetFromHref(href)) return

    event.preventDefault()
    scrollToHash(href, { behavior: 'smooth', updateHash: true })
  }

  /**
   * Активной считается последняя секция, чей верх уже прошёл под шапку.
   */
  #onScroll = () => {
    const offset = getAnchorOffset({ scrollDirection: 'down' })
    let active = null

    for (const pair of this.#pairs) {
      if (pair.section.getBoundingClientRect().top - offset <= 0) {
        active = pair.item
      }
    }

    // До первой секции подсвечиваем первый пункт: пустое меню в начале страницы
    // читалось бы как «ни один раздел не выбран», хотя выбрать нечего.
    this.#setActive(active || this.#pairs[0].item)
  }

  /**
   * @param {HTMLAnchorElement} item — пункт, который надо подсветить
   */
  #setActive(item) {
    if (this.#active === item) return

    this.#active?.classList.remove('is-active')
    item.classList.add('is-active')
    this.#active = item
  }
}
